import { API_BASE_URL } from '../../services/api';

export interface AsyncProcessingJob {
  jobId: string;
  fileName: string;
  status: 'processing' | 'completed' | 'failed';
  documentType?: string;
  message?: string;
  errorMessage?: string;
  createdAt: string;
  completedAt?: string;
  processingTime?: number;
  result?: {
    recordId: number;
    state: string;
    data: any;
  };
}

export interface ProcessingResult {
  fileName: string;
  status: string;
  documentType?: string;
  message: string;
  data?: any;
}

/**
 * Upload a file for async processing (returns immediately with job ID)
 */
export const uploadFileAsync = async (file: File): Promise<{ jobId: string; statusEndpoint: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/documentupload/process-and-save-single-async`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Failed to upload ${file.name}`);
  }

  const result = await response.json();
  
  console.log(`[asyncProcessing] Job created: ${result.jobId} for file ${file.name}`);
  
  return result;
};

/**
 * Check the status of a processing job
 */
export const checkJobStatus = async (jobId: string): Promise<AsyncProcessingJob | null> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/documentupload/job-status/${jobId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Handle 404 - Job not found (deleted or never existed)
  if (response.status === 404) {
    console.warn(`[asyncProcessing] Job ${jobId} not found (404)`);
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to check job status: ${response.status}`);
  }

  const job = await response.json();
  console.log(`[asyncProcessing] Job ${jobId} status: ${job.status}`);
  
  return job;
};

/**
 * Poll for job completion with exponential backoff
 */
export const pollJobUntilComplete = async (
  jobId: string,
  onProgress?: (job: AsyncProcessingJob) => void,
  maxAttempts: number = 120, // 120 attempts = up to 10 minutes with exponential backoff
): Promise<AsyncProcessingJob> => {
  let attempt = 0;
  
  while (attempt < maxAttempts) {
    const job = await checkJobStatus(jobId);
    
    // If job not found (deleted), throw error
    if (job === null) {
      throw new Error('Job not found - it may have been deleted');
    }
    
    if (onProgress) {
      onProgress(job);
    }

    if (job.status === 'completed' || job.status === 'failed') {
      return job;
    }

    // Exponential backoff: 1s, 2s, 4s, 5s, 5s, 5s...
    const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    attempt++;
  }

  throw new Error('Processing timeout: Job did not complete within expected time');
};

/**
 * Get all jobs for the current user from backend
 */
export const getUserJobs = async (): Promise<AsyncProcessingJob[]> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_BASE_URL}/documentupload/jobs`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
  throw new Error('Failed to fetch user jobs');
  }

  const jobs = await response.json();
  console.log(`[asyncProcessing] Retrieved ${jobs.length} jobs from database`);
  
  return jobs;
};

export interface JobHistoryFilters {
  days?: number;
  documentType?: string;
  source?: string;
  status?: string;
}

export interface JobHistoryJob {
  jobId: string;
  fileName: string;
  source: string;
  status: 'processing' | 'completed' | 'failed';
  documentType?: string;
  message?: string;
  errorMessage?: string;
  createdAt: string;
  completedAt?: string;
  processingTime?: number;
  canRetry: boolean;
  recordId?: number;
  recordState?: string;
}

export interface JobHistoryResponse {
  total: number;
  completed: number;
  failed: number;
  processing: number;
  filters: JobHistoryFilters;
  jobs: JobHistoryJob[];
}

/**
 * Get document processing job history with optional filters
 */
export const getJobHistory = async (filters: JobHistoryFilters = {}): Promise<JobHistoryResponse> => {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams();
  if (filters.days !== undefined) params.append('days', String(filters.days));
  if (filters.documentType) params.append('documentType', filters.documentType);
  if (filters.source) params.append('source', filters.source);
  if (filters.status) params.append('status', filters.status);

  const response = await fetch(`${API_BASE_URL}/documentupload/job-history?${params}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw new Error('Failed to fetch job history');
  return response.json();
};

/**
 * Retry a failed job in-place (resets the same job, does not create a new one)
 */
export const retryJob = async (jobId: string): Promise<{ jobId: string; message: string }> => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/documentupload/job/${jobId}/retry`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || 'Failed to retry job');
  }
  return response.json();
};

/**
 * Delete a job
 */
export const deleteJob = async (jobId: string): Promise<void> => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/documentupload/job/${jobId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || 'Failed to delete job');
  }
};

/**
 * Process a single file with async pattern and progress callback
 */
export const processSingleFileAsync = async (
  file: File,
  onProgress?: (progress: { status: string; message: string; percent?: number }) => void
): Promise<ProcessingResult> => {
  try {
    // Start upload
    if (onProgress) {
      onProgress({ status: 'uploading', message: 'Uploading file...', percent: 10 });
    }

    const { jobId } = await uploadFileAsync(file);

    if (onProgress) {
      onProgress({ status: 'processing', message: 'Processing document...', percent: 30 });
    }

    // Poll for completion
    const result = await pollJobUntilComplete(jobId, (job) => {
      if (onProgress) {
        const percent = job.status === 'processing' 
? Math.min(30 + (job.processingTime || 0) / 600 * 60, 90) // Scale 30-90% over 10 minutes
          : job.status === 'completed' ? 100 : 30;
        
        onProgress({
          status: job.status,
     message: job.message || 'Processing...',
      percent: Math.round(percent)
        });
      }
  });

    if (result.status === 'failed') {
  throw new Error(result.errorMessage || 'Processing failed');
 }

    if (onProgress) {
      onProgress({ status: 'completed', message: 'Processing complete!', percent: 100 });
    }

    return {
      fileName: result.fileName,
 status: result.status === 'completed' ? 'Success' : 'Failed',
      documentType: result.documentType,
      message: result.message || '',
      data: result.result
    };
  } catch (error: any) {
    if (onProgress) {
      onProgress({ status: 'failed', message: error.message || 'Processing failed', percent: 0 });
    }
    throw error;
  }
};

/**
 * Get status badge class for job status
 */
export const getJobStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'completed':
  return 'badge bg-success';
    case 'failed':
      return 'badge bg-danger';
    case 'processing':
      return 'badge bg-warning';
    default:
      return 'badge bg-secondary';
  }
};

/**
 * Format processing time
 */
export const formatProcessingTime = (seconds?: number): string => {
  if (!seconds) return 'N/A';
  
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

// ============================================================================
// DEBUGGING UTILITIES (can be called from browser console)
// ============================================================================

/**
 * Get diagnostic info about jobs from database
 */
export const getJobsDiagnostics = async () => {
  try {
    const jobs = await getUserJobs();
  const active = jobs.filter(j => j.status === 'processing');
    const completed = jobs.filter(j => j.status === 'completed' || j.status === 'failed');
    
    console.log('=== Document Processing Jobs Diagnostics (Database Only) ===');
    console.log(`Active jobs: ${active.length}`);
    active.forEach(job => {
      console.log(`  - ${job.jobId} (${job.fileName}) - ${job.status}`);
    });
    
    console.log(`Completed jobs: ${completed.length}`);
    completed.forEach(job => {
      console.log(`  - ${job.jobId} (${job.fileName}) - ${job.status}`);
    });
    
    return { active, completed, total: jobs.length };
  } catch (error) {
    console.error('Failed to get diagnostics:', error);
    return null;
  }
};
